import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Storefront, MapPin, MagnifyingGlass, Heart, NavigationArrow,
  GameController, Trophy, UsersThree, Star, CalendarDots, Funnel
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ShopLocatorPage() {
  const [shops, setShops] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('search');
  const [filters, setFilters] = useState({ city: '', state: '', game: '', beginner: false, openPlay: false, tournaments: false });
  const navigate = useNavigate();

  const favIds = new Set(favorites.map(f => f.id));

  const fetchShops = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.city) params.append('city', filters.city);
      if (filters.state) params.append('state', filters.state);
      if (filters.game) params.append('game', filters.game);
      if (filters.beginner) params.append('beginner_friendly', 'true');
      if (filters.openPlay) params.append('has_open_play', 'true');
      if (filters.tournaments) params.append('has_tournaments', 'true');

      const [searchRes, favRes] = await Promise.all([
        axios.get(`${API}/api/shop-locator/search?${params}`, { withCredentials: true }),
        axios.get(`${API}/api/shop-locator/favorites`, { withCredentials: true }),
      ]);
      setShops(searchRes.data);
      setFavorites(favRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { fetchShops(); }, [fetchShops]);

  const toggleFav = async (shopId) => {
    try {
      if (favIds.has(shopId)) {
        await axios.delete(`${API}/api/shop-locator/favorites/${shopId}`, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/shop-locator/favorites`, { shop_id: shopId }, { withCredentials: true });
      }
      fetchShops();
    } catch (e) { console.error(e); }
  };

  const ShopCard = ({ s }) => (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-4 hover:border-[#10B981]/30 transition-colors"
      data-testid={`shop-card-${s.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/shops/${s.id}`)}>
          <div className="w-10 h-10 rounded-lg bg-[#10B981]/10 flex items-center justify-center flex-shrink-0">
            <Storefront size={20} weight="duotone" className="text-[#10B981]" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className="text-[14px] font-semibold text-white truncate">{s.name}</h3>
              {s.verified && <Star size={12} weight="fill" className="text-[#F59E0B] flex-shrink-0" />}
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-[#555]">
              <MapPin size={11} /> {s.city}{s.state ? `, ${s.state}` : ''}
              {s.distance_miles !== null && <span className="text-[#10B981]">({s.distance_miles} mi)</span>}
            </div>
          </div>
        </div>
        <button onClick={() => toggleFav(s.id)} className="p-1.5 rounded-md hover:bg-white/[0.05] transition-colors"
          data-testid={`fav-btn-${s.id}`}>
          <Heart size={16} weight={favIds.has(s.id) ? 'fill' : 'regular'} className={favIds.has(s.id) ? 'text-[#EF4444]' : 'text-[#555]'} />
        </button>
      </div>

      {s.description && <p className="text-[11px] text-[#555] mb-2 line-clamp-1">{s.description}</p>}

      <div className="flex flex-wrap gap-1.5 mb-3">
        {s.beginner_friendly && <span className="text-[9px] bg-[#10B981]/10 text-[#10B981] px-1.5 py-0.5 rounded-full border border-[#10B981]/20">Beginner</span>}
        {s.hosts_tournaments && <span className="text-[9px] bg-[#F59E0B]/10 text-[#F59E0B] px-1.5 py-0.5 rounded-full border border-[#F59E0B]/20">Tournaments</span>}
        {s.open_for_free_play && <span className="text-[9px] bg-[#3B82F6]/10 text-[#3B82F6] px-1.5 py-0.5 rounded-full border border-[#3B82F6]/20">Open Play</span>}
      </div>

      <div className="flex gap-2">
        <a href={s.directions?.google_maps} target="_blank" rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-1 h-7 rounded-md bg-[#10B981]/10 border border-[#10B981]/20 text-[#10B981] text-[10px] font-medium hover:bg-[#10B981]/20 transition-colors"
          data-testid={`directions-${s.id}`}>
          <NavigationArrow size={12} /> Directions
        </a>
        <button onClick={() => navigate(`/shops/${s.id}`)}
          className="flex-1 flex items-center justify-center gap-1 h-7 rounded-md bg-white/[0.04] border border-white/[0.08] text-[#888] text-[10px] font-medium hover:text-white hover:border-white/[0.15] transition-colors"
          data-testid={`view-shop-${s.id}`}>
          <Storefront size={12} /> View Shop
        </button>
      </div>
    </motion.div>
  );

  return (
    <div className="max-w-4xl mx-auto" data-testid="shop-locator-page">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Shop Locator</h1>
        <p className="text-[13px] text-[#444] mt-0.5">Find Cyboard-registered game shops near you</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-[#0c0c14] rounded-lg p-1 w-fit border border-white/[0.06]">
        {[{ id: 'search', label: 'Search' }, { id: 'favorites', label: 'Favorites' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-md text-[12px] font-medium transition-colors ${tab === t.id ? 'bg-[#10B981]/15 text-[#10B981]' : 'text-[#555] hover:text-[#999]'}`}
            data-testid={`tab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {/* Filters */}
      {tab === 'search' && (
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-[120px]">
            <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#333]" />
            <input value={filters.city} onChange={e => setFilters({ ...filters, city: e.target.value })} placeholder="City..."
              className="w-full bg-[#0c0c14] border border-white/[0.06] rounded-md pl-9 pr-3 py-2 text-[13px] text-white focus:border-[#10B981]/30 focus:outline-none placeholder-[#2a2a2a]"
              data-testid="filter-city" />
          </div>
          <input value={filters.state} onChange={e => setFilters({ ...filters, state: e.target.value })} placeholder="State..."
            className="w-20 bg-[#0c0c14] border border-white/[0.06] rounded-md px-3 py-2 text-[13px] text-white focus:outline-none placeholder-[#2a2a2a]"
            data-testid="filter-state" />
          <div className="relative flex-1 min-w-[120px]">
            <GameController size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#333]" />
            <input value={filters.game} onChange={e => setFilters({ ...filters, game: e.target.value })} placeholder="Game..."
              className="w-full bg-[#0c0c14] border border-white/[0.06] rounded-md pl-9 pr-3 py-2 text-[13px] text-white focus:border-[#10B981]/30 focus:outline-none placeholder-[#2a2a2a]"
              data-testid="filter-game" />
          </div>
          <div className="flex gap-1.5 items-center">
            {[
              { key: 'beginner', label: 'Beginner', active: filters.beginner },
              { key: 'openPlay', label: 'Open Play', active: filters.openPlay },
              { key: 'tournaments', label: 'Tournaments', active: filters.tournaments },
            ].map(f => (
              <button key={f.key} onClick={() => setFilters({ ...filters, [f.key]: !f.active })}
                className={`px-2.5 py-1.5 rounded-md text-[10px] font-medium border transition-colors ${f.active ? 'bg-[#10B981]/10 border-[#10B981]/30 text-[#10B981]' : 'bg-transparent border-white/[0.06] text-[#555] hover:text-white'}`}
                data-testid={`toggle-${f.key}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-36 bg-white/[0.03] rounded-lg animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(tab === 'search' ? shops : favorites).map(s => <ShopCard key={s.id} s={s} />)}
          {(tab === 'search' ? shops : favorites).length === 0 && (
            <div className="col-span-2 text-center py-12 text-[#444]">
              <Storefront size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-[13px]">{tab === 'search' ? 'No shops found' : 'No favorite shops yet'}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
